import React, { useState, useEffect, useCallback, useLayoutEffect } from 'react';
import {
  Typography,
  Space,
  Button,
  message,
  Spin,
  Modal,
  Tooltip,
  Input,
} from 'antd';
import {
  FolderOutlined,
  FolderOpenOutlined,
  FileOutlined,
  FilePdfOutlined,
  ReloadOutlined,
  ArrowLeftOutlined,
  DownloadOutlined,
  ExpandOutlined,
  FileImageOutlined,
  ExportOutlined,
  RightOutlined,
  SearchOutlined,
  CloseCircleOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  getAccTopFolders,
  getAccFolderContents,
  getAccItemUrn,
  searchAccProject,
  AccSearchResult,
} from '../../../services/api';
import { ProtectedRoute, PageGuard } from '../../../components/common/security/ProtectedRoute';
import AutodeskViewer from '../../../components/common/AutodeskViewer';
import { useAuth } from '../../../components/common/security/AuthContext';

// Bu sayfayı tam ekran yapmak için CSS
const fullWidthStyle = `
  .content-container:has(.acc-project-detail-fullwidth) {
    max-width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
  }
`;

const { Title, Text } = Typography;

interface FolderNode {
  id: string;
  name: string;
  type: 'folder' | 'file';
  fileType?: string;
  size?: number;
  isExpanded?: boolean;
  isSelected?: boolean;
  children?: FolderNode[];
  items?: FolderNode[];
  loaded?: boolean;
}

interface SelectedFile {
  id: string;
  name: string;
  type: string;
  urn?: string;
}

// Tree Node Component
const TreeNode: React.FC<{
  node: FolderNode;
  level: number;
  onToggle: (id: string) => void;
  onSelect: (node: FolderNode) => void;
}> = ({ node, level, onToggle, onSelect }) => {
  const isFolder = node.type === 'folder';
  const paddingLeft = level * 16 + 8;

  const getIcon = () => {
    if (isFolder) {
      return node.isExpanded ? (
        <FolderOpenOutlined style={{ color: '#1890ff', fontSize: 16 }} />
      ) : (
        <FolderOutlined style={{ color: '#1890ff', fontSize: 16 }} />
      );
    }
    if (node.fileType === 'pdf') {
      return <FilePdfOutlined style={{ color: '#ff4d4f', fontSize: 16 }} />;
    }
    if (node.fileType === 'image') {
      return <FileImageOutlined style={{ color: '#13c2c2', fontSize: 16 }} />;
    }
    if (['cad', 'revit', 'navisworks', 'ifc', '3d'].includes(node.fileType || '')) {
      return <FileOutlined style={{ color: '#722ed1', fontSize: 16 }} />;
    }
    return <FileOutlined style={{ color: '#8c8c8c', fontSize: 16 }} />;
  };

  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div>
      <div
        onClick={() => {
          if (isFolder) {
            onToggle(node.id);
          } else {
            onSelect(node);
          }
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 12px',
          paddingLeft,
          cursor: 'pointer',
          borderRadius: 6,
          backgroundColor: node.isSelected ? '#e6f7ff' : 'transparent',
          color: node.isSelected ? '#1890ff' : '#333',
          transition: 'all 0.2s',
        }}
        onMouseEnter={(e) => {
          if (!node.isSelected) {
            e.currentTarget.style.backgroundColor = '#f5f5f5';
          }
        }}
        onMouseLeave={(e) => {
          if (!node.isSelected) {
            e.currentTarget.style.backgroundColor = 'transparent';
          }
        }}
      >
        {isFolder && (
          <RightOutlined
            style={{
              fontSize: 10,
              color: '#999',
              transition: 'transform 0.2s',
              transform: node.isExpanded ? 'rotate(90deg)' : 'rotate(0)',
              flexShrink: 0,
            }}
          />
        )}
        {!isFolder && <div style={{ width: 10, flexShrink: 0 }} />}
        {getIcon()}
        <Tooltip title={node.name} placement="right" mouseEnterDelay={0.5}>
          <span
            style={{
              flex: 1,
              fontSize: 13,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            {node.name}
          </span>
        </Tooltip>
        {!isFolder && node.size && (
          <span style={{ fontSize: 11, color: '#999', flexShrink: 0, marginLeft: 8 }}>{formatFileSize(node.size)}</span>
        )}
      </div>

      {/* Children (subfolders) */}
      {isFolder && node.isExpanded && node.children && node.children.length > 0 && (
        <div>
          {node.children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              level={level + 1}
              onToggle={onToggle}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}

      {/* Items (files) */}
      {isFolder && node.isExpanded && node.items && node.items.length > 0 && (
        <div>
          {node.items.map((item) => (
            <TreeNode
              key={item.id}
              node={item}
              level={level + 1}
              onToggle={onToggle}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const AccProjectDetail: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const hubId = searchParams.get('hubId') || undefined;
  const navigate = useNavigate();
  const { token } = useAuth();

  const [loading, setLoading] = useState(false);
  const [treeLoading, setTreeLoading] = useState(false);
  const [project, setProject] = useState<any>(null);
  const [treeData, setTreeData] = useState<FolderNode[]>([]);
  const [selectedFile, setSelectedFile] = useState<SelectedFile | null>(null);
  const [fileUrn, setFileUrn] = useState<string | null>(null);
  const [urnLoading, setUrnLoading] = useState(false);
  const [fullscreenModal, setFullscreenModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<AccSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchLimitReached, setSearchLimitReached] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const searchTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Debounced search - 500ms bekle
  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    
    // Önceki timeout'u iptal et
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    if (!query.trim() || query.trim().length < 2) {
      setIsSearchMode(false);
      setSearchResults([]);
      setSearchLimitReached(false);
      return;
    }
    
    // 500ms sonra ara
    searchTimeoutRef.current = setTimeout(async () => {
      if (!projectId || !hubId) return;
      
      setSearchLoading(true);
      setIsSearchMode(true);
      
      try {
        const result = await searchAccProject(projectId, query, hubId);
        if (result.success && result.results) {
          setSearchResults(result.results);
          setSearchLimitReached((result as any).limit_reached || false);
        } else {
          setSearchResults([]);
          setSearchLimitReached(false);
          if (result.error) {
            message.error('Arama hatası: ' + result.error);
          }
        }
      } catch (error: any) {
        console.error('Arama hatası:', error);
        setSearchResults([]);
        setSearchLimitReached(false);
      } finally {
        setSearchLoading(false);
      }
    }, 500);
  }, [projectId, hubId]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (projectId) {
      loadProjectAndFolders();
    }
  }, [projectId]);

  const loadProjectAndFolders = async () => {
    try {
      setLoading(true);

      // Get project from session storage
      const storedProject = sessionStorage.getItem('selectedProject');
      if (storedProject) {
        const projectData = JSON.parse(storedProject);
        setProject(projectData);
      }

      // Load top folders
      await loadTopFolders();
    } catch (error: any) {
      message.error('Proje yüklenirken hata: ' + (error.message || 'Bilinmeyen hata'));
    } finally {
      setLoading(false);
    }
  };

  const loadTopFolders = async () => {
    try {
      setTreeLoading(true);
      const result = await getAccTopFolders(projectId!, hubId);

      if (result.success && result.data) {
        const folders = result.data.data || [];

        // Find "Project Files" folder
        const projectFilesFolder = folders.find((folder: any) => {
          const folderName = folder.attributes?.displayName || folder.attributes?.name || '';
          return folderName.toLowerCase() === 'project files';
        });

        if (projectFilesFolder) {
          const rootNode: FolderNode = {
            id: projectFilesFolder.id,
            name: projectFilesFolder.attributes?.displayName || 'Project Files',
            type: 'folder',
            isExpanded: true,
            loaded: false,
          };

          setTreeData([rootNode]);

          // Auto-load Project Files contents
          await loadFolderContents(projectFilesFolder.id, [rootNode]);
        } else {
          // Show all top folders
          const nodes: FolderNode[] = folders.map((folder: any) => ({
            id: folder.id,
            name: folder.attributes?.displayName || folder.attributes?.name || 'İsimsiz Klasör',
            type: 'folder' as const,
            isExpanded: false,
            loaded: false,
          }));
          setTreeData(nodes);
        }
      }
    } catch (error: any) {
      message.error('Klasörler yüklenirken hata: ' + (error.message || 'Bilinmeyen hata'));
    } finally {
      setTreeLoading(false);
    }
  };

  const getFileType = (filename: string): string => {
    const ext = filename.split('.').pop()?.toLowerCase() || '';
    if (['pdf'].includes(ext)) return 'pdf';
    if (['dwg', 'dxf'].includes(ext)) return 'cad';
    if (['rvt', 'rfa', 'rte'].includes(ext)) return 'revit';
    if (['nwd', 'nwc', 'nwf'].includes(ext)) return 'navisworks';
    if (['ifc', 'ifczip'].includes(ext)) return 'ifc';
    if (['3ds', 'obj', 'fbx', 'stp', 'step', 'iges', 'igs'].includes(ext)) return '3d';
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tif', 'tiff'].includes(ext)) return 'image';
    return 'other';
  };

  const loadFolderContents = async (folderId: string, currentTree?: FolderNode[]) => {
    const tree = currentTree || treeData;

    try {
      const result = await getAccFolderContents(projectId!, folderId, hubId);

      if (result.success && result.data) {
        const contents = result.data.data || [];

        const subfolders: FolderNode[] = [];
        const items: FolderNode[] = [];

        contents.forEach((item: any) => {
          const name = item.attributes?.displayName || item.attributes?.name || 'İsimsiz';
          const isFolder = item.type === 'folders';

          if (isFolder) {
            subfolders.push({
              id: item.id,
              name,
              type: 'folder',
              isExpanded: false,
              loaded: false,
            });
          } else {
            const fileType = getFileType(name);
            items.push({
              id: item.id,
              name,
              type: 'file',
              fileType,
              size: item.attributes?.storageSize,
            });
          }
        });

        // Update tree
        const updateNode = (nodes: FolderNode[]): FolderNode[] => {
          return nodes.map((node) => {
            if (node.id === folderId) {
              return {
                ...node,
                children: subfolders,
                items,
                isExpanded: true,
                loaded: true,
              };
            }
            if (node.children) {
              return { ...node, children: updateNode(node.children) };
            }
            return node;
          });
        };

        setTreeData(updateNode(tree));
      }
    } catch (error) {
      console.error('Klasör içeriği yüklenirken hata:', error);
    }
  };

  const handleToggleFolder = async (folderId: string) => {
    const findNode = (nodes: FolderNode[]): FolderNode | null => {
      for (const node of nodes) {
        if (node.id === folderId) return node;
        if (node.children) {
          const found = findNode(node.children);
          if (found) return found;
        }
      }
      return null;
    };

    const node = findNode(treeData);
    if (!node) return;

    // If already loaded, just toggle
    if (node.loaded) {
      const toggleNode = (nodes: FolderNode[]): FolderNode[] => {
        return nodes.map((n) => {
          if (n.id === folderId) {
            return { ...n, isExpanded: !n.isExpanded };
          }
          if (n.children) {
            return { ...n, children: toggleNode(n.children) };
          }
          return n;
        });
      };
      setTreeData(toggleNode(treeData));
      return;
    }

    // Load contents
    await loadFolderContents(folderId);
  };

  const handleSelectFile = async (node: FolderNode) => {
    // Clear previous selection
    const clearSelection = (nodes: FolderNode[]): FolderNode[] => {
      return nodes.map((n) => ({
        ...n,
        isSelected: false,
        children: n.children ? clearSelection(n.children) : undefined,
        items: n.items ? clearSelection(n.items) : undefined,
      }));
    };

    // Set new selection
    const setSelection = (nodes: FolderNode[]): FolderNode[] => {
      return nodes.map((n) => {
        if (n.id === node.id) {
          return { ...n, isSelected: true };
        }
        return {
          ...n,
          children: n.children ? setSelection(n.children) : undefined,
          items: n.items ? setSelection(n.items) : undefined,
        };
      });
    };

    setTreeData(setSelection(clearSelection(treeData)));

    setSelectedFile({
      id: node.id,
      name: node.name,
      type: node.fileType || 'other',
    });

    // Load URN for viewable files
    const viewableTypes = ['pdf', 'cad', 'revit', 'navisworks', 'ifc', '3d'];
    if (viewableTypes.includes(node.fileType || '')) {
      setUrnLoading(true);
      setFileUrn(null);

      try {
        const urnResult = await getAccItemUrn(projectId!, node.id);
        if (urnResult.success && urnResult.urn) {
          setFileUrn(urnResult.urn);
        } else {
          console.warn('URN alınamadı:', urnResult.error);
        }
      } catch (error: any) {
        console.error('URN yüklenirken hata:', error);
      } finally {
        setUrnLoading(false);
      }
    } else {
      setFileUrn(null);
    }
  };

  const openInAcc = () => {
    if (!projectId) return;
    const cleanProjectId = projectId.startsWith('b.') ? projectId.substring(2) : projectId;
    const accUrl = `https://acc.autodesk.com/docs/files/projects/${cleanProjectId}`;
    window.open(accUrl, '_blank');
  };

  const getDownloadUrl = (itemId: string) => {
    const apiBase = `http://${window.location.hostname}:5000/api`;
    return `${apiBase}/acc/projects/${encodeURIComponent(projectId!)}/items/${encodeURIComponent(itemId)}/download?token=${encodeURIComponent(token || '')}`;
  };

  const getFileTypeLabel = (type: string) => {
    switch (type) {
      case 'pdf':
        return 'PDF Dosyası';
      case 'cad':
        return 'AutoCAD Dosyası';
      case 'revit':
        return 'Revit Dosyası';
      case 'navisworks':
        return 'Navisworks Dosyası';
      case 'ifc':
        return 'IFC Dosyası';
      case '3d':
        return '3D Model';
      case 'image':
        return 'Görsel';
      default:
        return 'Dosya';
    }
  };

  // Tam ekran CSS'i inject et
  useLayoutEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.id = 'acc-project-detail-fullwidth-style';
    styleEl.textContent = fullWidthStyle;
    document.head.appendChild(styleEl);
    
    return () => {
      const el = document.getElementById('acc-project-detail-fullwidth-style');
      if (el) el.remove();
    };
  }, []);

  return (
    <ProtectedRoute>
      <PageGuard>
        <div 
          className="acc-project-detail-fullwidth"
          style={{ 
            height: 'calc(100vh - 64px)',
            display: 'flex', 
            flexDirection: 'column', 
            backgroundColor: '#f5f5f5',
          }}
        >
          {/* Header */}
          <div
            style={{
              backgroundColor: '#fff',
              borderBottom: '1px solid #e8e8e8',
              padding: '16px 24px',
              boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
            }}
          >
            <button
              onClick={() => navigate('/design/acc/projects')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                color: '#666',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                marginBottom: 12,
                padding: 0,
                fontSize: 14,
              }}
            >
              <ArrowLeftOutlined />
              <span>Projelere Dön</span>
            </button>

            {loading ? (
              <div style={{ height: 32, width: 200, backgroundColor: '#f0f0f0', borderRadius: 4 }} />
            ) : project ? (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <Title level={3} style={{ margin: 0 }}>
                    {project.name}
                  </Title>
                  <Text type="secondary">Hub: {project.hubName || project.hubId}</Text>
                </div>
                <Space>
                  <Button icon={<ReloadOutlined />} onClick={loadProjectAndFolders} loading={loading}>
                    Yenile
                  </Button>
                </Space>
              </div>
            ) : (
              <Text type="danger">Proje yüklenemedi</Text>
            )}
          </div>

          {/* Main Content - Resizable Split Layout */}
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
            {/* Left Panel - Folder Tree */}
            {!sidebarCollapsed && (
              <div
                style={{
                  width: 380,
                  minWidth: 280,
                  maxWidth: 500,
                  height: '100%',
                  backgroundColor: '#fff',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  borderRight: '1px solid #e8e8e8',
                  resize: 'horizontal',
                }}
              >
              {/* Tree Header with Search */}
              <div
                style={{
                  padding: '12px 16px',
                  borderBottom: '1px solid #f0f0f0',
                  backgroundColor: '#fafafa',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <Text strong style={{ fontSize: 14 }}>
                    {isSearchMode ? 'Arama Sonuçları' : 'Klasörler ve Dosyalar'}
                  </Text>
                  <Tooltip title="Paneli Gizle">
                    <Button
                      type="text"
                      size="small"
                      icon={<MenuFoldOutlined />}
                      onClick={() => setSidebarCollapsed(true)}
                      style={{ color: '#8c8c8c' }}
                    />
                  </Tooltip>
                </div>
                <Input
                  placeholder="Tüm projede ara..."
                  prefix={<SearchOutlined style={{ color: searchLoading ? '#1890ff' : '#bfbfbf' }} />}
                  suffix={
                    searchQuery ? (
                      <CloseCircleOutlined
                        style={{ color: '#bfbfbf', cursor: 'pointer' }}
                        onClick={() => {
                          setSearchQuery('');
                          setIsSearchMode(false);
                          setSearchResults([]);
                          setSearchLimitReached(false);
                        }}
                      />
                    ) : null
                  }
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  size="small"
                  allowClear={false}
                  style={{ borderRadius: 6 }}
                />
                {isSearchMode && !searchLoading && (
                  <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>
                    {searchResults.length} sonuç bulundu
                    {searchLimitReached && ' (limit aşıldı, daha spesifik arayın)'}
                  </Text>
                )}
                {searchLoading && (
                  <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>
                    Aranıyor...
                  </Text>
                )}
              </div>

              {/* Tree/Search Content */}
              <div 
                style={{ 
                  flex: 1, 
                  overflowY: 'auto', 
                  overflowX: 'auto',
                  padding: '8px 0',
                }}
              >
                {treeLoading || searchLoading ? (
                  <div style={{ padding: 24, textAlign: 'center' }}>
                    <Spin />
                    <div style={{ marginTop: 12 }}>
                      <Text type="secondary">
                        {searchLoading ? 'Aranıyor...' : 'Klasörler yükleniyor...'}
                      </Text>
                    </div>
                  </div>
                ) : isSearchMode ? (
                  // Arama sonuçları listesi
                  searchResults.length > 0 ? (
                    <div>
                      {searchResults.map((result) => (
                        <div
                          key={result.id}
                          onClick={() => {
                            if (result.type === 'file') {
                              const fileType = getFileType(result.name);
                              handleSelectFile({
                                id: result.id,
                                name: result.name,
                                type: 'file',
                                fileType,
                              });
                            }
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '10px 16px',
                            cursor: result.type === 'file' ? 'pointer' : 'default',
                            borderBottom: '1px solid #f5f5f5',
                            backgroundColor: selectedFile?.id === result.id ? '#e6f7ff' : 'transparent',
                            transition: 'background-color 0.2s',
                          }}
                          onMouseEnter={(e) => {
                            if (result.type === 'file') {
                              e.currentTarget.style.backgroundColor = selectedFile?.id === result.id ? '#e6f7ff' : '#fafafa';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = selectedFile?.id === result.id ? '#e6f7ff' : 'transparent';
                          }}
                        >
                          {result.type === 'folder' ? (
                            <FolderOutlined style={{ color: '#1890ff', fontSize: 16 }} />
                          ) : result.extension?.toLowerCase() === 'pdf' ? (
                            <FilePdfOutlined style={{ color: '#ff4d4f', fontSize: 16 }} />
                          ) : ['dwg', 'dxf', 'rvt', 'rfa', 'ifc', 'nwc', 'nwd', 'nwf', '3ds', 'obj', 'fbx', 'stp', 'step'].includes(result.extension?.toLowerCase() || '') ? (
                            <FileOutlined style={{ color: '#722ed1', fontSize: 16 }} />
                          ) : (
                            <FileOutlined style={{ color: '#8c8c8c', fontSize: 16 }} />
                          )}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Tooltip title={result.name} placement="right" mouseEnterDelay={0.5}>
                              <div style={{ fontWeight: 500, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{result.name}</div>
                            </Tooltip>
                            <Tooltip title={result.path} placement="right" mouseEnterDelay={0.5}>
                              <div style={{ fontSize: 11, color: '#999', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {result.path}
                              </div>
                            </Tooltip>
                          </div>
                          {result.type === 'file' && (
                            <RightOutlined style={{ color: '#d9d9d9', fontSize: 10 }} />
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ padding: 40, textAlign: 'center' }}>
                      <SearchOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
                      <div>
                        <Text type="secondary">"{searchQuery}" için sonuç bulunamadı</Text>
                      </div>
                    </div>
                  )
                ) : treeData.length > 0 ? (
                  // Normal tree view
                  treeData.map((node) => (
                    <TreeNode
                      key={node.id}
                      node={node}
                      level={0}
                      onToggle={handleToggleFolder}
                      onSelect={handleSelectFile}
                    />
                  ))
                ) : (
                  <div style={{ padding: 40, textAlign: 'center' }}>
                    <FolderOpenOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
                    <div>
                      <Text type="secondary">Klasör bulunamadı</Text>
                    </div>
                  </div>
                )}
              </div>
              </div>
            )}

            {/* Right Panel - Viewer */}
            <div style={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                {/* Sidebar Toggle Button (visible when collapsed) */}
                {sidebarCollapsed && (
                  <Tooltip title="Klasörleri Göster" placement="right">
                    <Button
                      type="text"
                      icon={<MenuUnfoldOutlined />}
                      onClick={() => setSidebarCollapsed(false)}
                      style={{
                        position: 'absolute',
                        left: 0,
                        top: 12,
                        zIndex: 10,
                        backgroundColor: '#fff',
                        border: '1px solid #e8e8e8',
                        borderLeft: 'none',
                        borderRadius: '0 6px 6px 0',
                        boxShadow: '2px 0 8px rgba(0,0,0,0.1)',
                      }}
                    />
                  </Tooltip>
                )}
              {selectedFile ? (
                <>
                  {/* Viewer Header */}
                  <div
                    style={{
                      backgroundColor: '#fff',
                      borderBottom: '1px solid #e8e8e8',
                      padding: '12px 24px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.03)',
                    }}
                  >
                    <Space>
                      {selectedFile.type === 'pdf' ? (
                        <FilePdfOutlined style={{ fontSize: 20, color: '#1890ff' }} />
                      ) : (
                        <FileOutlined style={{ fontSize: 20, color: '#1890ff' }} />
                      )}
                      <div>
                        <Text strong>{selectedFile.name}</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {getFileTypeLabel(selectedFile.type)}
                        </Text>
                      </div>
                    </Space>
                    <Space>
                      <Button
                        icon={<DownloadOutlined />}
                        onClick={() => window.open(getDownloadUrl(selectedFile.id), '_blank')}
                      >
                        İndir
                      </Button>
                      <Button icon={<ExportOutlined />} onClick={openInAcc}>
                        ACC'de Aç
                      </Button>
                      {fileUrn && (
                        <Button icon={<ExpandOutlined />} onClick={() => setFullscreenModal(true)}>
                          Tam Ekran
                        </Button>
                      )}
                    </Space>
                  </div>

                  {/* Viewer Content */}
                  <div style={{ flex: 1, padding: 24, overflow: 'hidden' }}>
                    <div
                      style={{
                        height: '100%',
                        borderRadius: 12,
                        backgroundColor: '#fff',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        border: '1px solid #e8e8e8',
                        overflow: 'hidden',
                      }}
                    >
                      {urnLoading ? (
                        <div
                          style={{
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Space direction="vertical" align="center">
                            <Spin size="large" />
                            <Text type="secondary">Dosya yükleniyor...</Text>
                          </Space>
                        </div>
                      ) : fileUrn && !fullscreenModal ? (
                        <AutodeskViewer
                          key={`viewer-${fileUrn}`}
                          urn={fileUrn}
                          style={{ height: '100%' }}
                        />
                      ) : fullscreenModal ? (
                        <div
                          style={{
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#fafafa',
                          }}
                        >
                          <Space direction="vertical" align="center">
                            <ExpandOutlined style={{ fontSize: 48, color: '#1890ff' }} />
                            <Text type="secondary">Tam ekran modunda görüntüleniyor</Text>
                          </Space>
                        </div>
                      ) : (
                        <div
                          style={{
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Space direction="vertical" align="center">
                            <FileOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />
                            <Text>Bu dosya türü için önizleme mevcut değil</Text>
                            <Button
                              type="primary"
                              icon={<DownloadOutlined />}
                              onClick={() => window.open(getDownloadUrl(selectedFile.id), '_blank')}
                            >
                              Dosyayı İndir
                            </Button>
                          </Space>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        width: 128,
                        height: 128,
                        backgroundColor: '#f5f5f5',
                        borderRadius: 24,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 24px',
                      }}
                    >
                      <FileOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
                    </div>
                    <Title level={4} style={{ marginBottom: 8 }}>
                      Dosya Seçilmedi
                    </Title>
                    <Text type="secondary">Soldan bir dosya seçerek görüntülemeye başlayın</Text>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Fullscreen Modal */}
          <Modal
            title={selectedFile?.name}
            open={fullscreenModal}
            onCancel={() => setFullscreenModal(false)}
            footer={null}
            width="95vw"
            style={{ top: 20 }}
            styles={{ body: { height: 'calc(95vh - 110px)', padding: 0 } }}
            destroyOnClose
          >
            {fullscreenModal && fileUrn && (
              <AutodeskViewer
                key={`fullscreen-${fileUrn}`}
                urn={fileUrn}
                onViewerReady={(viewer) => console.log('✅ Fullscreen Viewer hazır:', viewer)}
                onViewerError={(error) => {
                  console.error('Fullscreen Viewer hatası:', error);
                  message.error('Tam ekran viewer yüklenemedi');
                }}
                style={{ height: '100%' }}
              />
            )}
          </Modal>
        </div>
      </PageGuard>
    </ProtectedRoute>
  );
};

export default AccProjectDetail;
